/**
 * Request Logger Service
 * Handles request/response logging and monitoring utilities
 */

import { APIRequest, APIResponse } from './APIGateway';
import { IntegrationError } from './ErrorHandler';

export interface LogEntry {
  id: string;
  timestamp: Date;
  type: 'request' | 'response' | 'error' | 'retry';
  requestId: string;
  clientId?: string;
  method?: string;
  url?: string;
  statusCode?: number;
  duration?: number;
  error?: string;
  retryAttempt?: number;
  metadata?: Record<string, any>;
}

export interface RequestMetrics {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  averageResponseTime: number;
  requestsPerMinute: number;
  errorRate: number;
  topErrors: ErrorSummary[];
  slowestEndpoints: EndpointMetrics[];
}

export interface ErrorSummary {
  error: string;
  count: number;
  percentage: number;
  lastOccurrence: Date;
}

export interface EndpointMetrics {
  endpoint: string;
  averageResponseTime: number;
  requestCount: number;
  errorRate: number;
}

export class RequestLogger {
  private logs: LogEntry[] = [];
  private maxLogEntries: number = 10000;
  private enabled: boolean;
  private metricsCache: RequestMetrics | null = null;
  private metricsCacheExpiry: Date | null = null;
  private readonly CACHE_DURATION = 60000; // 1 minute

  constructor(enabled: boolean = true, maxLogEntries: number = 10000) {
    this.enabled = enabled;
    this.maxLogEntries = maxLogEntries;
  }

  /**
   * Log an incoming request
   */
  async logRequest(requestId: string, request: APIRequest, clientId?: string): Promise<void> {
    if (!this.enabled) return;

    const logEntry: LogEntry = {
      id: this.generateLogId(),
      timestamp: new Date(),
      type: 'request',
      requestId,
      clientId,
      method: request.method,
      url: this.sanitizeUrl(request.url),
      metadata: {
        headers: this.sanitizeHeaders(request.headers),
        bodySize: request.body ? JSON.stringify(request.body).length : 0,
        timeout: request.timeout,
        retryAttempts: request.retryAttempts
      }
    };

    this.addLogEntry(logEntry);
  }

  /**
   * Log a response
   */
  async logResponse(requestId: string, response: APIResponse): Promise<void> {
    if (!this.enabled) return;

    const logEntry: LogEntry = {
      id: this.generateLogId(),
      timestamp: new Date(),
      type: 'response',
      requestId,
      statusCode: response.status,
      duration: response.duration,
      metadata: {
        headers: this.sanitizeHeaders(response.headers),
        dataSize: response.data ? JSON.stringify(response.data).length : 0
      }
    };

    this.addLogEntry(logEntry);
    this.invalidateMetricsCache();
  }

  /**
   * Log an error
   */
  async logError(requestId: string, error: IntegrationError): Promise<void> {
    if (!this.enabled) return;

    const logEntry: LogEntry = {
      id: this.generateLogId(),
      timestamp: new Date(),
      type: 'error',
      requestId,
      statusCode: error.statusCode,
      error: error.message,
      metadata: {
        errorType: error.type,
        errorCode: error.code,
        retryable: error.retryable,
        context: error.context
      }
    };

    this.addLogEntry(logEntry);
    this.invalidateMetricsCache();
  }

  /**
   * Log a retry attempt
   */
  async logRetry(requestId: string, attempt: number, delay: number): Promise<void> {
    if (!this.enabled) return;

    const logEntry: LogEntry = {
      id: this.generateLogId(),
      timestamp: new Date(),
      type: 'retry',
      requestId,
      retryAttempt: attempt,
      metadata: {
        delay,
        nextAttemptAt: new Date(Date.now() + delay)
      }
    };

    this.addLogEntry(logEntry);
  }

  /**
   * Get logs with optional filtering
   */
  getLogs(filter?: LogFilter): LogEntry[] {
    let filteredLogs = [...this.logs];

    if (filter) {
      if (filter.requestId) {
        filteredLogs = filteredLogs.filter(log => log.requestId === filter.requestId);
      }

      if (filter.clientId) {
        filteredLogs = filteredLogs.filter(log => log.clientId === filter.clientId);
      }

      if (filter.type) {
        filteredLogs = filteredLogs.filter(log => log.type === filter.type);
      }

      if (filter.startTime) {
        filteredLogs = filteredLogs.filter(log => log.timestamp >= filter.startTime!);
      }

      if (filter.endTime) {
        filteredLogs = filteredLogs.filter(log => log.timestamp <= filter.endTime!);
      }

      if (filter.statusCode) {
        filteredLogs = filteredLogs.filter(log => log.statusCode === filter.statusCode);
      }

      if (filter.hasError !== undefined) {
        filteredLogs = filteredLogs.filter(log => 
          filter.hasError ? log.type === 'error' : log.type !== 'error'
        );
      }
    }

    // Sort by timestamp (newest first)
    return filteredLogs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  /**
   * Get request metrics
   */
  async getMetrics(timeRange?: { start: Date; end: Date }): Promise<RequestMetrics> {
    // Check cache first
    if (this.metricsCache && this.metricsCacheExpiry && this.metricsCacheExpiry > new Date()) {
      return this.metricsCache;
    }

    const logs = timeRange ? 
      this.getLogs({ startTime: timeRange.start, endTime: timeRange.end }) : 
      this.logs;

    const requestLogs = logs.filter(log => log.type === 'request');
    const responseLogs = logs.filter(log => log.type === 'response');
    const errorLogs = logs.filter(log => log.type === 'error');

    const totalRequests = requestLogs.length;
    const successfulRequests = responseLogs.filter(log => 
      log.statusCode && log.statusCode >= 200 && log.statusCode < 400
    ).length;
    const failedRequests = totalRequests - successfulRequests;

    // Calculate average response time
    const responseTimes = responseLogs
      .filter(log => log.duration !== undefined)
      .map(log => log.duration!);
    const averageResponseTime = responseTimes.length > 0 ? 
      responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length : 0;

    // Calculate requests per minute
    const timeSpan = timeRange ? 
      (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60) : 
      this.getLogTimeSpan();
    const requestsPerMinute = timeSpan > 0 ? totalRequests / timeSpan : 0;

    // Calculate error rate
    const errorRate = totalRequests > 0 ? failedRequests / totalRequests : 0;

    // Get top errors
    const topErrors = this.getTopErrors(errorLogs);

    // Get slowest endpoints
    const slowestEndpoints = this.getSlowestEndpoints(requestLogs, responseLogs);

    const metrics: RequestMetrics = {
      totalRequests,
      successfulRequests,
      failedRequests,
      averageResponseTime,
      requestsPerMinute,
      errorRate,
      topErrors,
      slowestEndpoints
    };

    // Cache the metrics
    this.metricsCache = metrics;
    this.metricsCacheExpiry = new Date(Date.now() + this.CACHE_DURATION);

    return metrics;
  }

  /**
   * Get total request count
   */
  async getTotalRequests(): Promise<number> {
    return this.logs.filter(log => log.type === 'request').length;
  }

  /**
   * Get success rate
   */
  async getSuccessRate(): Promise<number> {
    const metrics = await this.getMetrics();
    return metrics.totalRequests > 0 ? 
      metrics.successfulRequests / metrics.totalRequests : 0;
  }

  /**
   * Get average response time
   */
  async getAverageResponseTime(): Promise<number> {
    const metrics = await this.getMetrics();
    return metrics.averageResponseTime;
  }

  /**
   * Clear logs
   */
  clearLogs(): void {
    this.logs = [];
    this.invalidateMetricsCache();
  }

  /**
   * Export logs to different formats
   */
  exportLogs(format: 'json' | 'csv' | 'txt', filter?: LogFilter): string {
    const logs = this.getLogs(filter);

    switch (format) {
      case 'json':
        return JSON.stringify(logs, null, 2);

      case 'csv':
        return this.exportToCSV(logs);

      case 'txt':
        return this.exportToText(logs);

      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  /**
   * Set logging enabled/disabled
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if logging is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  private addLogEntry(entry: LogEntry): void {
    this.logs.push(entry);

    // Maintain max log entries limit
    if (this.logs.length > this.maxLogEntries) {
      this.logs = this.logs.slice(-this.maxLogEntries);
    }
  }

  private generateLogId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      // Remove sensitive query parameters
      const sensitiveParams = ['api_key', 'token', 'password', 'secret'];
      sensitiveParams.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });
      return urlObj.toString();
    } catch {
      return url; // Return original if URL parsing fails
    }
  }

  private sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
    const sanitized = { ...headers };
    const sensitiveHeaders = ['authorization', 'x-api-key', 'cookie', 'x-auth-token'];
    
    sensitiveHeaders.forEach(header => {
      const key = Object.keys(sanitized).find(k => k.toLowerCase() === header);
      if (key) {
        sanitized[key] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  private getLogTimeSpan(): number {
    if (this.logs.length === 0) return 0;
    
    const timestamps = this.logs.map(log => log.timestamp.getTime());
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    
    return (maxTime - minTime) / (1000 * 60); // Convert to minutes
  }

  private getTopErrors(errorLogs: LogEntry[]): ErrorSummary[] {
    const errorCounts = new Map<string, { count: number; lastOccurrence: Date }>();

    errorLogs.forEach(log => {
      if (log.error) {
        const existing = errorCounts.get(log.error) || { count: 0, lastOccurrence: log.timestamp };
        errorCounts.set(log.error, {
          count: existing.count + 1,
          lastOccurrence: log.timestamp > existing.lastOccurrence ? log.timestamp : existing.lastOccurrence
        });
      }
    });

    const totalErrors = errorLogs.length;
    return Array.from(errorCounts.entries())
      .map(([error, data]) => ({
        error,
        count: data.count,
        percentage: totalErrors > 0 ? (data.count / totalErrors) * 100 : 0,
        lastOccurrence: data.lastOccurrence
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 errors
  }

  private getSlowestEndpoints(requestLogs: LogEntry[], responseLogs: LogEntry[]): EndpointMetrics[] {
    const endpointMetrics = new Map<string, { 
      totalTime: number; 
      count: number; 
      errors: number; 
    }>();

    // Group by endpoint
    requestLogs.forEach(requestLog => {
      if (!requestLog.url) return;

      const endpoint = this.extractEndpoint(requestLog.url);
      const responseLog = responseLogs.find(r => r.requestId === requestLog.requestId);
      
      if (responseLog && responseLog.duration !== undefined) {
        const existing = endpointMetrics.get(endpoint) || { totalTime: 0, count: 0, errors: 0 };
        existing.totalTime += responseLog.duration;
        existing.count++;
        
        if (responseLog.statusCode && responseLog.statusCode >= 400) {
          existing.errors++;
        }
        
        endpointMetrics.set(endpoint, existing);
      }
    });

    return Array.from(endpointMetrics.entries())
      .map(([endpoint, data]) => ({
        endpoint,
        averageResponseTime: data.count > 0 ? data.totalTime / data.count : 0,
        requestCount: data.count,
        errorRate: data.count > 0 ? data.errors / data.count : 0
      }))
      .sort((a, b) => b.averageResponseTime - a.averageResponseTime)
      .slice(0, 10); // Top 10 slowest endpoints
  }

  private extractEndpoint(url: string): string {
    try {
      const urlObj = new URL(url);
      return `${urlObj.pathname}`;
    } catch {
      return url;
    }
  }

  private exportToCSV(logs: LogEntry[]): string {
    const headers = ['ID', 'Timestamp', 'Type', 'Request ID', 'Client ID', 'Method', 'URL', 'Status Code', 'Duration', 'Error'];
    const rows = logs.map(log => [
      log.id,
      log.timestamp.toISOString(),
      log.type,
      log.requestId,
      log.clientId || '',
      log.method || '',
      log.url || '',
      log.statusCode?.toString() || '',
      log.duration?.toString() || '',
      log.error || ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }

  private exportToText(logs: LogEntry[]): string {
    return logs.map(log => {
      const parts = [
        `[${log.timestamp.toISOString()}]`,
        `${log.type.toUpperCase()}`,
        `${log.requestId}`,
        log.clientId ? `Client: ${log.clientId}` : '',
        log.method && log.url ? `${log.method} ${log.url}` : '',
        log.statusCode ? `Status: ${log.statusCode}` : '',
        log.duration ? `Duration: ${log.duration}ms` : '',
        log.error ? `Error: ${log.error}` : ''
      ].filter(Boolean);

      return parts.join(' | ');
    }).join('\n');
  }

  private invalidateMetricsCache(): void {
    this.metricsCache = null;
    this.metricsCacheExpiry = null;
  }
}

export interface LogFilter {
  requestId?: string;
  clientId?: string;
  type?: 'request' | 'response' | 'error' | 'retry';
  startTime?: Date;
  endTime?: Date;
  statusCode?: number;
  hasError?: boolean;
}